/**
 * @mergeTarget
 * @module Src/Profiles
 */
import { LisioParameterFactory } from "../index.js";
import { LisioBooleanParameterNames } from "../parameter/lisio-boolean-parameter.js";
import { LisioNumericParameterNames } from "../parameter/lisio-numeric-parameter.js";
import { LisioStringParameterNames } from "../parameter/lisio-string-parameter.js";
import { CursorSizeOptions } from "../parameter/stringOptionsEnums/cursor-size-options.js";
import { FontFamilyOptions } from "../parameter/stringOptionsEnums/font-family-options.js";
import { ThemeOptions } from "../parameter/stringOptionsEnums/theme-options.js";
import { LisioProfilErrorCodes } from "../profil-error-codes.js";
import { LisioProfile, LisioProfileNames } from "./lisio-profile.js";
/**
 * ## How to create a new profile ? Part 2
 *
 * To create a new profile is very easy, if you don't create a new profile name go to {@link Src/Profiles.LisioProfileNames | LisioProfileNames}.\
 * Next go create a new builder for your profile, it only consists at choose which parameters your profile will use. You can take exemple on other builders.\
 * When it's done the final step is to add your new builder in {@link LisioProfileFactory._builders | LisioProfileFactory._builders}
 *
 * ## How to modify a profile ? Part 2
 *
 * To rename a profile is very easy, if you want to rename your profile and you don't choose a new profile name go to {@link Src/Profiles.LisioProfileNames | LisioProfileNames}.\
 * Next change all old profile name by your new profile name.\
 *
 * If you want to modify parameters used in profile, go to profile builder and modify it as you desire.
 *
 * ## How to delete a profile ? Part 2
 *
 * To delete a profile is very easy, if you don't delete profile name go to {@link Src/Profiles.LisioProfileNames | LisioProfileNames}.\
 * Next delete your profile builder.\
 * When it's done the final step is to remove your old builder from {@link LisioProfileFactory._builders | LisioProfileFactory._builders}
 *
 * ## Documentation
 *
 * Class representing a profile factory.\
 * It aims to centralize all instanciation of profile.\
 * To execute his responsability this class does :
 *  * Build a profile with right corresponding parameters
 */
class LisioProfileFactory {
    /**
     * Private attribute to store all profile builders
     * @source
     */
    _builders = new Map([
        [LisioProfileNames.ATTENTION, this.attentionBuilder],
        [LisioProfileNames.CATARACT, this.cataractBuilder],
        [LisioProfileNames.DICTIONARY, this.dictionaryBuilder],
        [LisioProfileNames.DYSLEXIA, this.dyslexiaBuilder],
        [LisioProfileNames.EPILEPSY, this.epilepsyBuilder],
        [LisioProfileNames.FOCUS, this.focusBuilder],
        [LisioProfileNames.GESTURES, this.gesturesBuilder],
        [LisioProfileNames.GLASS_X2, this.glassX2Builder],
        [LisioProfileNames.GLASS_X4, this.glassX4Builder],
        [LisioProfileNames.HEADACHE, this.headacheBuilder],
        [LisioProfileNames.INEXPERIENCE, this.inexperienceBuilder],
        [LisioProfileNames.LEARNING, this.learningBuilder],
        [LisioProfileNames.LOW_EYE_SIGHT, this.lowEyeSightBuilder],
        [LisioProfileNames.LOW_NETWORK, this.lowNetworkBuilder],
        [LisioProfileNames.MEMORY, this.memoryBuilder],
        [LisioProfileNames.MOVEMENTS, this.movementsBuilder],
        [LisioProfileNames.PRESBYOPIA, this.presbyopiaBuilder],
        [LisioProfileNames.READING, this.readingBuilder],
        [LisioProfileNames.UNDERLINE, this.underlineBuilder],
        [LisioProfileNames.VERY_LOW_EYE_SIGHT, this.veryLowEyeSightBuilder],
        [LisioProfileNames.VISION, this.visionBuilder],
        [LisioProfileNames.ECOLO, this.ecoBuilder],
        [LisioProfileNames.LIGHT_ECOLO, this.lightEcoBuilder],
        [LisioProfileNames.DARK_MODE, this.darkModeBuilder],
    ]);
    /**
     * @static
     * Private attribute to store instance of {@link LisioProfileFactory | LisioProfileFactory}
     * @source
     */
    static _current;
    /**
     * @static
     * Getter for attribute {@link _current | _current}
     * @returns Returns _current attribute
     * @source
     */
    static get current() {
        return LisioProfileFactory._current;
    }
    /**
     * Private method to build a dyslexia profile.\
     * This profile uses following parameters :
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.OPEN_DYSLEXIC = FontFamilyOptions.OPEN_DYSLEXIC}
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.WORD_SPACING | LisioNumericParameterNames.WORD_SPACING} = 5
     * @returns Returns a dyslexia profile
     * @source
     */
    dyslexiaBuilder() {
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.OPEN_DYSLEXIC);
        const wordSpacingParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.WORD_SPACING, 5);
        const dyslexiaParameters = new Map([
            [fontFamilyParameter.name, fontFamilyParameter],
            [wordSpacingParameter.name, wordSpacingParameter],
        ]);
        return new LisioProfile(LisioProfileNames.DYSLEXIA, dyslexiaParameters);
    }
    /**
     * Private method to build a glassX2 profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/String.LisioStringParameterNames.CURSOR_SIZE | LisioStringParameterNames.CURSOR_SIZE} = {@link Src/Parameters/String/Options.CursorSizeOptions.VERY_BIG = CursorSizeOptions.VERY_BIG}
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.ZOOM | LisioNumericParameterNames.ZOOM} = 2
     * @returns Returns a glassX2 profile
     * @source
     */
    glassX2Builder() {
        const cursorSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.CURSOR_SIZE, CursorSizeOptions.VERY_BIG);
        const zoomParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.ZOOM, 2);
        const glassX2Parameters = new Map([
            [cursorSizeParameter.name, cursorSizeParameter],
            [zoomParameter.name, zoomParameter],
        ]);
        return new LisioProfile(LisioProfileNames.GLASS_X2, glassX2Parameters);
    }
    /**
     * Private method to build a glassX4 profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/String.LisioStringParameterNames.CURSOR_SIZE | LisioStringParameterNames.CURSOR_SIZE} = {@link Src/Parameters/String/Options.CursorSizeOptions.VERY_BIG = CursorSizeOptions.VERY_BIG}
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.ZOOM | LisioNumericParameterNames.ZOOM} = 4
     * @returns Returns a glassX4 profile
     * @source
     */
    glassX4Builder() {
        const cursorSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.CURSOR_SIZE, CursorSizeOptions.VERY_BIG);
        const zoomParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.ZOOM, 4);
        const glassX2Parameters = new Map([
            [cursorSizeParameter.name, cursorSizeParameter],
            [zoomParameter.name, zoomParameter],
        ]);
        return new LisioProfile(LisioProfileNames.GLASS_X4, glassX2Parameters);
    }
    /**
     * Private method to build a epilepsy profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/String.LisioStringParameterNames.THEME | LisioStringParameterNames.THEME} = {@link Src/Parameters/String/Options.ThemeOptions.C_BK_BG_G = ThemeOptions.C_BK_BG_G}
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.FONT_SIZE | LisioNumericParameterNames.FONT_SIZE} = 2
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.DISABLE_ANIMATION | LisioBooleanParameterNames.DISABLE_ANIMATION = true}
     * @returns Returns a epilepsy profile
     * @source
     */
    epilepsyBuilder() {
        const themeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.THEME, ThemeOptions.C_BK_BG_G);
        const fontSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.FONT_SIZE, 2);
        const toggleAnimationParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.DISABLE_ANIMATION, true);
        const epilepsyParameters = new Map([
            [themeParameter.name, themeParameter],
            [fontSizeParameter.name, fontSizeParameter],
            [toggleAnimationParameter.name, toggleAnimationParameter],
        ]);
        return new LisioProfile(LisioProfileNames.EPILEPSY, epilepsyParameters);
    }
    /**
     * Private method to build a movements profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/String.LisioStringParameterNames.CURSOR_SIZE | LisioStringParameterNames.CURSOR_SIZE} = {@link Src/Parameters/String/Options.CursorSizeOptions.BIG = CursorSizeOptions.BIG}
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.BIGGER_CLICK | LisioNumericParameterNames.BIGGER_CLICK} = 4
     * @returns Returns a movements profile
     * @source
     */
    movementsBuilder() {
        const cursorSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.CURSOR_SIZE, CursorSizeOptions.BIG);
        const biggerClickParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.BIGGER_CLICK, 4);
        const movementsParameters = new Map([
            [cursorSizeParameter.name, cursorSizeParameter],
            [biggerClickParameter.name, biggerClickParameter],
        ]);
        return new LisioProfile(LisioProfileNames.MOVEMENTS, movementsParameters);
    }
    /**
     * Private method to build a gestures profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/String.LisioStringParameterNames.CURSOR_SIZE | LisioStringParameterNames.CURSOR_SIZE} = {@link Src/Parameters/String/Options.CursorSizeOptions.BIG = CursorSizeOptions.BIG}
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.BIGGER_CLICK | LisioNumericParameterNames.BIGGER_CLICK} = 4
     * @returns Returns a gestures profile
     * @source
     */
    gesturesBuilder() {
        const cursorSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.CURSOR_SIZE, CursorSizeOptions.BIG);
        const biggerClickParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.BIGGER_CLICK, 4);
        const gesturesParameters = new Map([
            [cursorSizeParameter.name, cursorSizeParameter],
            [biggerClickParameter.name, biggerClickParameter],
        ]);
        return new LisioProfile(LisioProfileNames.GESTURES, gesturesParameters);
    }
    /**
     * Private method to build a focus profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.DISABLE_ANIMATION | LisioBooleanParameterNames.DISABLE_ANIMATION} = true
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.READING_MASK | LisioBooleanParameterNames.READING_MASK} = true
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.ARIAL = FontFamilyOptions.ARIAL}
     * @returns Returns a focus profile
     * @source
     */
    focusBuilder() {
        const animationParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.DISABLE_ANIMATION, true);
        const readingMaskParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.READING_MASK, true);
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.ARIAL);
        const focusParameters = new Map([
            [animationParameter.name, animationParameter],
            [readingMaskParameter.name, readingMaskParameter],
            [fontFamilyParameter.name, fontFamilyParameter],
        ]);
        return new LisioProfile(LisioProfileNames.FOCUS, focusParameters);
    }
    /**
     * Private method to build a reading profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.FONT_SIZE | LisioNumericParameterNames.FONT_SIZE} = 2
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.LINE_HEIGHT | LisioNumericParameterNames.LINE_HEIGHT} = 1.5
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.WORD_SPACING | LisioNumericParameterNames.WORD_SPACING} = 5
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.ARIAL = FontFamilyOptions.ARIAL}
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns a reading profile
     * @source
     */
    readingBuilder() {
        const fontSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.FONT_SIZE, 2);
        const lineHeightParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.LINE_HEIGHT, 1.5);
        const wordSpacingParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.WORD_SPACING, 5);
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.ARIAL);
        const readingModeParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const readingParameters = new Map([
            [fontSizeParameter.name, fontSizeParameter],
            [lineHeightParameter.name, lineHeightParameter],
            [wordSpacingParameter.name, wordSpacingParameter],
            [fontFamilyParameter.name, fontFamilyParameter],
            [readingModeParameter.name, readingModeParameter],
        ]);
        return new LisioProfile(LisioProfileNames.READING, readingParameters);
    }
    /**
     * Private method to build a low eye sight profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.FONT_SIZE | LisioNumericParameterNames.FONT_SIZE} = 4
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.LINE_HEIGHT | LisioNumericParameterNames.LINE_HEIGHT} = 1.5
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.WORD_SPACING | LisioNumericParameterNames.WORD_SPACING} = 6
     *  * {@link Src/Parameters/String.LisioStringParameterNames.CURSOR_SIZE | LisioStringParameterNames.CURSOR_SIZE} = {@link Src/Parameters/String/Options.CursorSizeOptions.BIG = CursorSizeOptions.BIG}
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.LUCIOLE = FontFamilyOptions.LUCIOLE}
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns a low eye sight profile
     * @source
     */
    lowEyeSightBuilder() {
        const fontSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.FONT_SIZE, 4);
        const lineHeightParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.LINE_HEIGHT, 1.5);
        const wordSpacingParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.WORD_SPACING, 6);
        const cursorSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.CURSOR_SIZE, CursorSizeOptions.BIG);
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.LUCIOLE);
        const readingModeParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const lowEyeSightParameters = new Map([
            [fontSizeParameter.name, fontSizeParameter],
            [lineHeightParameter.name, lineHeightParameter],
            [wordSpacingParameter.name, wordSpacingParameter],
            [cursorSizeParameter.name, cursorSizeParameter],
            [fontFamilyParameter.name, fontFamilyParameter],
            [readingModeParameter.name, readingModeParameter],
        ]);
        return new LisioProfile(LisioProfileNames.LOW_EYE_SIGHT, lowEyeSightParameters);
    }
    /**
     * Private method to build a very low eye sight profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.FONT_SIZE | LisioNumericParameterNames.FONT_SIZE} = 6
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.LINE_HEIGHT | LisioNumericParameterNames.LINE_HEIGHT} = 2
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.WORD_SPACING | LisioNumericParameterNames.WORD_SPACING} = 10
     *  * {@link Src/Parameters/String.LisioStringParameterNames.CURSOR_SIZE | LisioStringParameterNames.CURSOR_SIZE} = {@link Src/Parameters/String/Options.CursorSizeOptions.VERY_BIG = CursorSizeOptions.VERY_BIG}
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.LUCIOLE = FontFamilyOptions.LUCIOLE}
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns a very low eye sight profile
     * @source
     */
    veryLowEyeSightBuilder() {
        const fontSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.FONT_SIZE, 6);
        const lineHeightParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.LINE_HEIGHT, 2);
        const wordSpacingParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.WORD_SPACING, 10);
        const cursorSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.CURSOR_SIZE, CursorSizeOptions.VERY_BIG);
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.LUCIOLE);
        const readingModeParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const veryLowEyeSightParameters = new Map([
            [fontSizeParameter.name, fontSizeParameter],
            [lineHeightParameter.name, lineHeightParameter],
            [wordSpacingParameter.name, wordSpacingParameter],
            [cursorSizeParameter.name, cursorSizeParameter],
            [fontFamilyParameter.name, fontFamilyParameter],
            [readingModeParameter.name, readingModeParameter],
        ]);
        return new LisioProfile(LisioProfileNames.VERY_LOW_EYE_SIGHT, veryLowEyeSightParameters);
    }
    /**
     * Private method to build a vision profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.FONT_SIZE | LisioNumericParameterNames.FONT_SIZE} = 4
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.LINE_HEIGHT | LisioNumericParameterNames.LINE_HEIGHT} = 1.5
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.WORD_SPACING | LisioNumericParameterNames.WORD_SPACING} = 5
     *  * {@link Src/Parameters/String.LisioStringParameterNames.CURSOR_SIZE | LisioStringParameterNames.CURSOR_SIZE} = {@link Src/Parameters/String/Options.CursorSizeOptions.BIG = CursorSizeOptions.BIG}
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.LUCIOLE = FontFamilyOptions.LUCIOLE}
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns a vision profile
     * @source
     */
    visionBuilder() {
        const fontSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.FONT_SIZE, 4);
        const lineHeightParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.LINE_HEIGHT, 1.5);
        const wordSpacingParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.WORD_SPACING, 5);
        const cursorSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.CURSOR_SIZE, CursorSizeOptions.BIG);
        const readingModeParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.LUCIOLE);
        const visionParameters = new Map([
            [fontSizeParameter.name, fontSizeParameter],
            [lineHeightParameter.name, lineHeightParameter],
            [wordSpacingParameter.name, wordSpacingParameter],
            [cursorSizeParameter.name, cursorSizeParameter],
            [readingModeParameter.name, readingModeParameter],
            [fontFamilyParameter.name, fontFamilyParameter],
        ]);
        return new LisioProfile(LisioProfileNames.VISION, visionParameters);
    }
    /**
     * Private method to build a presbyopia profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.FONT_SIZE | LisioNumericParameterNames.FONT_SIZE} = 2
     *  * {@link Src/Parameters/String.LisioStringParameterNames.THEME | LisioStringParameterNames.THEME} = {@link Src/Parameters/String/Options.ThemeOptions.C_BK_BG_W = ThemeOptions.C_BK_BG_W}
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.WORD_SPACING | LisioNumericParameterNames.WORD_SPACING} = 10
     *  * {@link Src/Parameters/String.LisioStringParameterNames.CURSOR_SIZE | LisioStringParameterNames.CURSOR_SIZE} = {@link Src/Parameters/String/Options.CursorSizeOptions.VERY_BIG = CursorSizeOptions.VERY_BIG}
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.LUCIOLE = FontFamilyOptions.LUCIOLE}
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns a presbyopia profile
     * @source
     */
    presbyopiaBuilder() {
        const fontSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.FONT_SIZE, 2);
        const themeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.THEME, ThemeOptions.C_BK_BG_W);
        const wordSpacingParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.WORD_SPACING, 10);
        const cursorSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.CURSOR_SIZE, CursorSizeOptions.VERY_BIG);
        const readingModeParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.LUCIOLE);
        const presbyopiaParameters = new Map([
            [fontSizeParameter.name, fontSizeParameter],
            [themeParameter.name, themeParameter],
            [wordSpacingParameter.name, wordSpacingParameter],
            [cursorSizeParameter.name, cursorSizeParameter],
            [readingModeParameter.name, readingModeParameter],
            [fontFamilyParameter.name, fontFamilyParameter],
        ]);
        return new LisioProfile(LisioProfileNames.PRESBYOPIA, presbyopiaParameters);
    }
    /**
     * Private method to build a cataract profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.FONT_SIZE | LisioNumericParameterNames.FONT_SIZE} = 3
     *  * {@link Src/Parameters/String.LisioStringParameterNames.THEME | LisioStringParameterNames.THEME} = {@link Src/Parameters/String/Options.ThemeOptions.C_BK_BG_G = ThemeOptions.C_BK_BG_G}
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.WORD_SPACING | LisioNumericParameterNames.WORD_SPACING} = 5
     *  * {@link Src/Parameters/String.LisioStringParameterNames.CURSOR_SIZE | LisioStringParameterNames.CURSOR_SIZE} = {@link Src/Parameters/String/Options.CursorSizeOptions.VERY_BIG = CursorSizeOptions.VERY_BIG}
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.LINE_HEIGHT | LisioNumericParameterNames.LINE_HEIGHT} = 1.5
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.HIGHLIGHT | LisioBooleanParameterNames.HIGHLIGHT} = true
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.LUCIOLE = FontFamilyOptions.LUCIOLE}
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns a cataract profile
     * @source
     */
    cataractBuilder() {
        const fontSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.FONT_SIZE, 3);
        const themeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.THEME, ThemeOptions.C_BK_BG_G);
        const wordSpacingParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.WORD_SPACING, 5);
        const cursorSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.CURSOR_SIZE, CursorSizeOptions.VERY_BIG);
        const lineHeightParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.LINE_HEIGHT, 1.5);
        const highlightParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.HIGHLIGHT, true);
        const readingModeParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.LUCIOLE);
        const cataractParameters = new Map([
            [fontSizeParameter.name, fontSizeParameter],
            [themeParameter.name, themeParameter],
            [wordSpacingParameter.name, wordSpacingParameter],
            [cursorSizeParameter.name, cursorSizeParameter],
            [lineHeightParameter.name, lineHeightParameter],
            [highlightParameter.name, highlightParameter],
            [readingModeParameter.name, readingModeParameter],
            [fontFamilyParameter.name, fontFamilyParameter],
        ]);
        return new LisioProfile(LisioProfileNames.CATARACT, cataractParameters);
    }
    /**
     * Private method to build a underline profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.OPEN_DYSLEXIC = FontFamilyOptions.OPEN_DYSLEXIC}
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.WORD_SPACING | LisioNumericParameterNames.WORD_SPACING} = 5
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.ACTIVE_UNDERLINE | LisioBooleanParameterNames.ACTIVE_UNDERLINE} = true
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns a underline profile
     * @source
     */
    underlineBuilder() {
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.OPEN_DYSLEXIC);
        const wordSpacingParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.WORD_SPACING, 5);
        const activeUnderlineParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.ACTIVE_UNDERLINE, true);
        const readingModeParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const underlineParameters = new Map([
            [fontFamilyParameter.name, fontFamilyParameter],
            [wordSpacingParameter.name, wordSpacingParameter],
            [activeUnderlineParameter.name, activeUnderlineParameter],
            [readingModeParameter.name, readingModeParameter],
        ]);
        return new LisioProfile(LisioProfileNames.UNDERLINE, underlineParameters);
    }
    /**
     * @deprecated
     * Private method to build a inexperience profile
     * @returns Returns a inexperience profile
     * @source
     */
    inexperienceBuilder() {
        const fontSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.FONT_SIZE, 2);
        const lineHeightParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.LINE_HEIGHT, 1.5);
        const wordSpacingParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.WORD_SPACING, 5);
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.ANDIKA);
        const inexperienceParameters = new Map([
            [fontSizeParameter.name, fontSizeParameter],
            [lineHeightParameter.name, lineHeightParameter],
            [wordSpacingParameter.name, wordSpacingParameter],
            [fontFamilyParameter.name, fontFamilyParameter],
        ]);
        return new LisioProfile(LisioProfileNames.INEXPERIENCE, inexperienceParameters);
    }
    /**
     * Private method to build a learning profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.FONT_SIZE | LisioNumericParameterNames.FONT_SIZE} = 2
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.LINE_HEIGHT | LisioNumericParameterNames.LINE_HEIGHT} = 1.5
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.WORD_SPACING | LisioNumericParameterNames.WORD_SPACING} = 5
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.ARIAL = FontFamilyOptions.ARIAL}
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns a learning profile
     * @source
     */
    learningBuilder() {
        const fontSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.FONT_SIZE, 2);
        const lineHeightParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.LINE_HEIGHT, 1.5);
        const wordSpacingParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.WORD_SPACING, 5);
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.ANDIKA);
        const readingModeParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const learningParameters = new Map([
            [fontSizeParameter.name, fontSizeParameter],
            [lineHeightParameter.name, lineHeightParameter],
            [wordSpacingParameter.name, wordSpacingParameter],
            [fontFamilyParameter.name, fontFamilyParameter],
            [readingModeParameter.name, readingModeParameter],
        ]);
        return new LisioProfile(LisioProfileNames.LEARNING, learningParameters);
    }
    /**
     * Private method to build an attention profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.FONT_SIZE | LisioNumericParameterNames.FONT_SIZE} = 2
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.LINE_HEIGHT | LisioNumericParameterNames.LINE_HEIGHT} = 1.5
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.WORD_SPACING | LisioNumericParameterNames.WORD_SPACING} = 5
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.ARIAL = FontFamilyOptions.ARIAL}
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns an attention profile
     * @source
     */
    attentionBuilder() {
        const fontSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.FONT_SIZE, 2);
        const lineHeightParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.LINE_HEIGHT, 1.5);
        const wordSpacingParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.WORD_SPACING, 5);
        const readingModeParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.ANDIKA);
        const attentionParameters = new Map([
            [fontSizeParameter.name, fontSizeParameter],
            [wordSpacingParameter.name, wordSpacingParameter],
            [lineHeightParameter.name, lineHeightParameter],
            [readingModeParameter.name, readingModeParameter],
            [fontFamilyParameter.name, fontFamilyParameter],
        ]);
        return new LisioProfile(LisioProfileNames.ATTENTION, attentionParameters);
    }
    /**
     * Private method to build a memory profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.FONT_SIZE | LisioNumericParameterNames.FONT_SIZE} = 2
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.LINE_HEIGHT | LisioNumericParameterNames.LINE_HEIGHT} = 1.5
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.WORD_SPACING | LisioNumericParameterNames.WORD_SPACING} = 5
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.ARIAL = FontFamilyOptions.ARIAL}
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns a memory profile
     * @source
     */
    memoryBuilder() {
        const fontSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.FONT_SIZE, 2);
        const lineHeightParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.LINE_HEIGHT, 1.5);
        const wordSpacingParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.WORD_SPACING, 5);
        const readingModeParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.ANDIKA);
        const memoryParameters = new Map([
            [fontSizeParameter.name, fontSizeParameter],
            [wordSpacingParameter.name, wordSpacingParameter],
            [lineHeightParameter.name, lineHeightParameter],
            [readingModeParameter.name, readingModeParameter],
            [fontFamilyParameter.name, fontFamilyParameter],
        ]);
        return new LisioProfile(LisioProfileNames.MEMORY, memoryParameters);
    }
    /**
     * Private method to build a headache profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Numeric.LisioNumericParameterNames.FONT_SIZE | LisioNumericParameterNames.FONT_SIZE} = 2
     *  * {@link Src/Parameters/String.LisioStringParameterNames.THEME | LisioStringParameterNames.THEME} = {@link Src/Parameters/String/Options.ThemeOptions.C_BK_BG_G = ThemeOptions.C_BK_BG_G}
     *  * {@link Src/Parameters/String.LisioStringParameterNames.FONT_FAMILY | LisioStringParameterNames.FONT_FAMILY} = {@link Src/Parameters/String/Options.FontFamilyOptions.ARIAL = FontFamilyOptions.ARIAL}
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns a headache profile
     * @source
     */
    headacheBuilder() {
        const fontSizeParameter = LisioParameterFactory.current.buildLisioParameter(LisioNumericParameterNames.FONT_SIZE, 2);
        const themeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.THEME, ThemeOptions.C_BK_BG_G);
        const fontFamilyParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions.ARIAL);
        const readingModeParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const headacheParameters = new Map([
            [fontSizeParameter.name, fontSizeParameter],
            [themeParameter.name, themeParameter],
            [fontFamilyParameter.name, fontFamilyParameter],
            [readingModeParameter.name, readingModeParameter],
        ]);
        return new LisioProfile(LisioProfileNames.HEADACHE, headacheParameters);
    }
    /**
     * Private method to build a low network profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns a low network profile
     * @source
     */
    lowNetworkBuilder() {
        const bookPageParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const lowNetworkParameters = new Map([
            [bookPageParameter.name, bookPageParameter],
        ]);
        return new LisioProfile(LisioProfileNames.LOW_NETWORK, lowNetworkParameters);
    }
    /**
     * Private method to build a dictionary profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.VOCA | LisioBooleanParameterNames.VOCA} = true
     * @returns Returns a dictionary profile
     * @source
     */
    dictionaryBuilder() {
        const vocaParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.VOCA, true);
        const dictionaryParameters = new Map([
            [vocaParameter.name, vocaParameter],
        ]);
        return new LisioProfile(LisioProfileNames.DICTIONARY, dictionaryParameters);
    }
    /**
     * Private method to build a eco profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/Boolean.LisioBooleanParameterNames.BOOK_PAGE | LisioBooleanParameterNames.BOOK_PAGE} = true
     * @returns Returns a eco profile
     * @source
     */
    ecoBuilder() {
        const bookPageParameter = LisioParameterFactory.current.buildLisioParameter(LisioBooleanParameterNames.BOOK_PAGE, true);
        const ecoParameters = new Map([[bookPageParameter.name, bookPageParameter]]);
        return new LisioProfile(LisioProfileNames.ECOLO, ecoParameters);
    }
    /**
     * Private method to build a light eco profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/String.LisioStringParameterNames.THEME | LisioStringParameterNames.THEME} = {@link Src/Parameters/String/Options.ThemeOptions.C_W_BG_BK = ThemeOptions.C_W_BG_BK}
     * @returns Returns a light eco profile
     * @source
     */
    lightEcoBuilder() {
        const themeParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.THEME, ThemeOptions.C_W_BG_BK);
        const lightEcoParameters = new Map([
            [themeParameter.name, themeParameter],
        ]);
        return new LisioProfile(LisioProfileNames.LIGHT_ECOLO, lightEcoParameters);
    }
    /**
     * Private method to build a dark mode profile
     * This profile uses following parameters :
     *  * {@link Src/Parameters/String.LisioStringParameterNames.THEME | LisioStringParameterNames.THEME} = {@link Src/Parameters/String/Options.ThemeOptions.C_W_BG_GY = ThemeOptions.C_W_BG_GY}
     * @returns Returns a dark mode profile
     * @source
     */
    darkModeBuilder() {
        const contrastParameter = LisioParameterFactory.current.buildLisioParameter(LisioStringParameterNames.THEME, ThemeOptions.C_W_BG_GY);
        const darkModeParameters = new Map([
            [contrastParameter.name, contrastParameter],
        ]);
        return new LisioProfile(LisioProfileNames.DARK_MODE, darkModeParameters);
    }
    /**
     * Public method to build a desired profile
     * @param {LisioProfileNames} profileName - Name of desired profile
     * @returns Retuns a lisio profile
     * @throws If profile doesn't exist.\
     * See {@link Src/Utils.LisioProfilErrorCodes.PROFILE_NOT_FOUND | LisioProfilErrorCodes.PROFILE_NOT_FOUND}
     * @source
     */
    buildLisioProfile(profileName) {
        const builder = this._builders.get(profileName);
        if (builder == undefined) {
            throw new Error(`Code : ${LisioProfilErrorCodes.PROFILE_NOT_FOUND}. Profile "${profileName}" not found`);
        }
        else {
            return builder();
        }
    }
    /**
     * Constructor of class {@link LisioProfileFactory | LisioProfileFactory}
     * @throws If singleton already initialize.\
     * See {@link https://env-preprod-docs.lisio.fr/lisio-engine/enums/Src_Core.CoreErrorCodes.html#SINGLETON_NOT_UNIQUE | CoreErrorCodes.SINGLETON_NOT_UNIQUE}
     * @source
     */
    constructor() {
        if (LisioProfileFactory._current == undefined) {
            LisioProfileFactory._current = this;
        }
        else {
            throw new Error(`Code : LisioCore_0. Singleton already initialize`);
        }
    }
}
export { LisioProfileFactory };
